import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';
import { AuthLayout } from './AuthLayout.js';

const MIN_PASSWORD_LENGTH = 12;

export function RegisterScreen(): ReactNode {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const registration = useQuery({
    queryKey: ['registration-status'],
    queryFn: () => api.get('/api/v1/auth/registration'),
    staleTime: 60_000,
  });

  const register = useMutation({
    mutationFn: () =>
      api.post('/api/v1/auth/register', {
        email,
        password,
        ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
      }),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    register.mutate();
  };

  if (registration.isPending) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  if (registration.data?.open !== true) {
    return (
      <AuthLayout title={t('auth.registration.closed.title')}>
        <Alert tone="info">{t('auth.registration.closed.body')}</Alert>
        <p className="form__aside">
          <a href="/sign-in">{t('auth.backToSignIn')}</a>
        </p>
      </AuthLayout>
    );
  }

  if (register.isSuccess) {
    // The same screen appears whether or not the address was already registered — the API
    // answers identically, and so must the interface.
    return (
      <AuthLayout title={t('auth.register.sent.title')}>
        <Alert tone="success">{t('auth.register.sent.body')}</Alert>
        <p className="form__aside">
          <a href="/sign-in">{t('auth.backToSignIn')}</a>
        </p>
      </AuthLayout>
    );
  }

  const error = register.error;

  return (
    <AuthLayout
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
      footer={
        <p>
          {t('auth.register.hasAccount')} <a href="/sign-in">{t('auth.signIn')}</a>
        </p>
      }
    >
      <form className="form" onSubmit={onSubmit} noValidate>
        {error === null || error === undefined ? null : (
          <Alert tone="critical">
            {error instanceof ApiError ? t(`error.${error.code}`) : t('error.SERVICE_UNAVAILABLE')}
          </Alert>
        )}

        <TextField
          label={t('auth.email')}
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          {...(error instanceof ApiError && error.fieldError('/email') !== undefined
            ? { error: error.fieldError('/email') }
            : {})}
        />

        <TextField
          label={t('auth.displayName')}
          name="displayName"
          autoComplete="name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <TextField
          label={t('auth.password')}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          hint={t('auth.register.passwordHint', { count: MIN_PASSWORD_LENGTH })}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(error instanceof ApiError && error.fieldError('/password') !== undefined
            ? { error: error.fieldError('/password') }
            : {})}
        />

        <Button type="submit" variant="primary" loading={register.isPending}>
          {t('auth.register.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
